<script>
import { ref }       from "vue";
import PageHeader    from "@/components/page-header";
import Response      from  "@/components/response";
import Review        from "@/components/review";
import Filters       from "@/components/filter";
import axios         from "axios";
import Utils         from "@/js/utils";
import DataUtils     from "@/js/data-utils";
import FeedbackUtils from "@/js/feedback-utils";
import RepUtils      from "@/js/reputation-utils";
import sources       from "@/data/sources";
import ReviewReply   from "@/components/review-reply";
import ResponseReply from "@/components/response-reply";
import DocsButton    from "@/components/documentation-button";

export default {
  data() {
    return {
      filterCols: ["daterange", "group_companies", "has_sentiment", "topics", "with_responses", "companies", "rating_range", "sources", "reviewer_country", "travel_composition", "language", "page", "source_type", "packages"],
      params: { page: 1 },
      loading: false,
      totalItems: 0,
      itemsPerPage: 20,
      totalPages: 1,
      cache: {},
      api_permissions: [],
      yesNoOptions: { true: 'yes', false: 'no' },
      translations: { [this.$i18n.locale]: {} },
      filterParams: {},
      replyPermissions: {},
      sources: {},
      connectionLink: null,
      feedbackContracts: [],
      user: {},
      additionalInfoFields: {
        review:   [
          { field: "reviewer_name", i18nPath: "name" },
          { field: "travel_composition_name", i18nPath: "travel_composition" },
          { field: "creation_date", i18nPath: "date" },
          { field: "package", i18nPath: "package" },
          { field: "helpful_votes", i18nPath: "helpful_votes" },
          { field: "price", i18nPath: "price" },
          { field: "room_name", i18nPath: "room_name" },
          { field: "reviewer_country", i18nPath: "country" },
          { field: "source_name", i18nPath: "source" },
          { field: "language_name_translated", i18nPath: "language" },
        ],
        response: [
          { field: "additional_info", subField: "guest-name", i18nPath: "name" },
          { field: "duration", i18nPath: "duration" },
          { field: "language", subField: "code", i18nPath: "language" },
          { field: "created_at", i18nPath: "date" },
          { field: "additional_info", subField: "guest-arrival_date", i18nPath: "arrival" },
          { field: "additional_info", subField: "guest-departure_date", i18nPath: "departure" },
          { field: "additional_info", subField: "guest-booking-number", i18nPath: "booking_number" },
          { field: "additional_info", subField: "guest-email", i18nPath: "email" },
          { field: "additional_info", subField: "guest-hostel", i18nPath: "hostel" },
          { field: "additional_info", subField: "guest-market", i18nPath: "market" },
          { field: "additional_info", subField: "guest-region", i18nPath: "region" },
          { field: "additional_info", subField: "guest-reservation_type", i18nPath: "reservation_type" },
          { field: "survey", subField: "name", i18nPath: "survey" },
          { field: "company", subField: "name", i18nPath: "company" }
        ]
      },
      polaritiesToShow: ["positive", /*"neutral",*/ "negative"],
      modals: ref({ reply: { review: false, response: false }, note: false, extension_settings: false }),
      note: "",
      loadingNote: false,
      emailUsers: true,
      selectedItemId: ref(null),
      generateResponse: false,
      replyText: "",
      idOnReviewSite: null,
      sendReviewReply: false,
      responseSettings: {
        tonality: "professional",
        customTonality: "",
        language: {
          use_review_language: false,
          customLanguage: this.$i18n.locale
        },
        mention_property_name: {
          enabled: true,
          value: null,
        },
        sign_name: {
          enabled: true,
          value: null,
        },
        limit_words: {
          enabled: true,
          value: null,
        },
        words_to_mention: {
          enabled: true,
          selected: [],
          options: []
        },
        saved_responses: {
          value: null,
          options: []
        }
      },
    };
  },
  components: { PageHeader, Filters, Response, Review, ReviewReply, ResponseReply, DocsButton },
  props: ["insideExtension", "extensionParams"],
  provide() {
    return {
      modals:            this.modals,
      selectedItemId:    this.selectedItemId,
      setSelectedItemId: id => this.selectedItemId = id
    };
  },
  created() {
    this.$store.dispatch("user/fetch").then(data => {
      this.user = data;
      this.api_permissions   = data.api_permissions;
      this.feedbackContracts = data.feedback_contracts;
    });
    if (this.insideExtension) window.addEventListener("message", this.handleExtensionMessages);
  },
  mounted() {
    window.addEventListener("scroll", this.scrollBtnDisplay);
    window.addEventListener("keydown", this.navigateBetweenItems);
    this.sources = sources;
  },
  beforeUnmount() {
    window.removeEventListener("scroll", this.scrollBtnDisplay);
    window.removeEventListener("keydown", this.navigateBetweenItems);
  },
  methods: {
    ...DataUtils,
    ...FeedbackUtils,
    ...RepUtils,
    ...Utils.extension,
    async loadListener(params) {
      this.cache = {};
      // correct the path for the topics filter
      params.data.topic = params.data.topics;
      let st = params.data.source_type;
      st = Array.isArray(st) ? st[0] : st;
      this.params = {
        page : Array.isArray(params.data.page) ? parseInt(params.data.page[0] || 1) : parseInt(params.data.page || 1),
        limit: this.itemsPerPage,
        compositions: params.data.travel_composition,
        countries:    params.data.reviewer_country,
        languages:    params.data.language,
        ...(st != "all" && { type: st }),
        ...(params.data.with_responses != "all" && { with_response: params.data.with_responses == "with" }),
        ...Utils.slice(params.data, ["start_date", "end_date", "company_ids", "group_ids", "rating_range", "sources", "has_sentiment", "topic", "packages"])
      };
      this.params = { ...this.params, ...Utils.slice(params.data, ["sources", "topic", "compositions", "countries", "languages", "packages"].map(f => `exclude_${f}`)) };

      await this.loadFeed();
      this.note = "";
    },
    async changePage() {
      if (!this.cache[this.params.page]) await this.loadFeed();
      this.resetExpandButtons();
    },
    async loadFeed() {
      Utils.setLoading.bind(this)(true);
      this.cache[this.params.page] = [];
      let currentPageParams = Utils.deepClone(this.params);
      if (this.insideExtension && this.$route.query.is_ota == "true")
        currentPageParams = { ...Utils.slice(currentPageParams, ["company_ids", "sources", "with_response"]), limit: 50 };
      let response = await axios.get("/v3/reviews/feed", { params: { ...currentPageParams, notes: true } });

      let items = response.data.data;
      items.forEach(item => {
        ["translated"].forEach(field => item[field] = false);
        item.show = true;
        if (item.source) item.source_name = this.sources[item.source].friendly_name;
      });

      this.cache[this.params.page] = items;
      this.totalItems = response.data.count;
      this.totalPages = Math.ceil(this.totalItems / this.itemsPerPage);

      Utils.setLoading.bind(this)(false);
      await this.loadReplyPermissions();
      this.scrollToItem();

      return this.cache[this.params.page];
    },
    async loadReplyPermissions() {
      let promises = [], ids = [];
      this.cache[this.params.page].filter(item => item.type == "review").forEach(review => {
        if (Utils.permittedReviewSources.includes(review.source) && review.company.id && (this.replyPermissions[review.company.id] == undefined)) ids.push(review.company.id);
      });

      ids = [...new Set(ids)];
      ids.forEach(id => {
        promises.push(axios.get(`/v3/oauth/credentials/${id}/active`).then(res => this.replyPermissions[id] = res.data.data?.length > 0));
      });
      await Promise.all(promises);
    },
    async pageChange() {
      await this.$router.push({ path: this.$route.path, query: { ...this.$route.query, "pg": this.params.page } });
      this.filterParams.data.page = this.params.page;
      await this.changePage();
      this.$nextTick(() => window.scrollTo(0, 0));
    },
    scrollBtnDisplay() {
      let scrollBtn = document.querySelector("#scroll-btn")
      window.scrollY > window.innerHeight
        ? scrollBtn?.classList.add("show")
        : scrollBtn?.classList.remove("show");
    },
    scrollWindow(x = 0, y = 0) {
      setTimeout(() => {
        window.scrollTo({
          top: y,
          left: x,
          behavior: "smooth",
        });
      }, 10);
    },
    async downloadXLSX() {
      Utils.setLoading.bind(this)(true);
      let response = await axios.get("/v3/reviews/feed.xlsx", { params: { ...this.params, limit: null, page: null }, responseType: "blob" });
      let blob = new Blob([response.data], { type: "" });
      let link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${this.$t("daily_operations.reviews.title", { p: this.params.page })}.xlsx`;
      link.click();
      URL.revokeObjectURL(link.href);
      Utils.setLoading.bind(this)(false);
    },
    updateReply(review) {
      const item = this.cache[this.params.page].find(i => i.id == this.selectedItemId);
      if (!item) return;
      const commentIndex = item.comments.findIndex(c => c.kind == "manager");
      if (commentIndex < 0)
        item.comments.push({ ...review.comment, kind: "manager" });
      else {
        item.comments.splice(commentIndex, 1, { ...review.comment, kind: "manager" });
      }
    },
    selectItem(id) {
      this.selectedItemId = id;
      this.generateResponse = false;
      this.$router.replace({ query: { ...this.$route.query, rid: id } });
    },
    scrollToItem(offset = 0) {
      if (!(this.selectedItemId && this.$refs[this.selectedItemId])) return;
      const review = Array.isArray(this.$refs[this.selectedItemId]) ? this.$refs[this.selectedItemId][0] : this.$refs[this.selectedItemId];
      if (!review) return;
      const top = review.$el.getBoundingClientRect().top + window.scrollY - offset;
      this.scrollWindow(0, top);
    },
    navigateBetweenItems(ev) {
      const reviews = this.cache[this.params.page];
      if (!/ArrowUp|ArrowDown/.test(ev.key) || !reviews?.length) return;
      const itemIndex = this.cache[this.params.page]?.findIndex(i => i.id == this.selectedItemId);
      let newIndex = itemIndex;
      if (ev.key == "ArrowUp")   newIndex -= 1;
      if (ev.key == "ArrowDown") newIndex += 1;
      if (newIndex >= this.itemsPerPage || newIndex < 0 || newIndex >= this.totalItems) return;
      if (reviews[newIndex]) this.selectItem(reviews[newIndex].id);
      this.scrollToItem(100);
    },
    async handleExtensionMessages(event) {
      if (Object.keys(this.extensionEvents).includes(event.data?.type))
        this.extensionEvents[event.data.type](event.data)
    },
    async generateResponseFromExtension(data) {
      const review = data.review || {};
      this.replyText = "";
      if (this.selectedItem?.id_on_review_site != review.id_on_review_site) this.closeReplyModal();
      else this.generateResponse = false;
      this.selectedItemId = null;
      await this.$nextTick();
      this.setItemByIdOnReviewSite(review.id_on_review_site);
      this.generateResponse = true;
    },
    async sendReviewReplyFromExtension(data) {
      const { review, text } = data?.data || {};
      if (!review?.id_on_review_site || !text?.length) return;
      const isSameReview = this.selectedItem?.id_on_review_site == review.id_on_review_site;

      if (this.selectedItemId && !isSameReview) {
        this.closeReplyModal();
        this.selectedItemId = null;
      }

      this.sendReviewReply = false;
      this.replyText = text;
      await this.$nextTick();

      if (!isSameReview)
        this.setItemByIdOnReviewSite(review.id_on_review_site);
      this.sendReviewReply = true;
    },
    setItemByIdOnReviewSite(id) {
      this.selectedItemId = this.cache[this.params.page]?.find(r => r.id_on_review_site == id)?.id;
    },
    openReplyModal(timeOut = 0) {
      if (!this.selectedItem?.id) return;
      setTimeout(() => {
        this.setDomainCookie();
        this.modals.reply[this.selectedItem.type] = true
      }, timeOut);
    },
    closeReplyModal() {
      this.modals.reply = { review: false, response: false };
    },
    async saveNote() {
      this.loadingNote = true;
      const params = {
        body:        this.note,
        email_users: this.emailUsers ? "1" : undefined,
        review_url:  this.reviewLink(this.selectedItem),
        review_id:   this.selectedItem.review_id
      };
      await axios.post(`/v2/companies/${this.selectedItem.company.id}/reviews/${this.selectedItem.review_id}/notes`, params);
      if (Array.isArray(this.selectedItem.notes)) this.selectedItem.notes.push({ body: this.note, date: Date(), user: this.user.name });
      else this.selectedItem.notes = [{ body: this.note, date: Date(), user: this.user.name }];
      this.closeNoteModal();
      this.loadingNote = false;
    },
    closeNoteModal() {
      this.modals.note = false;
      this.note = "";
    },
    triggerFilterSubmit() {
      if (this.$refs.filtersRef) this.$refs.filtersRef.readAndSubmit();
    }
  },
  computed: {
    contract() {
      return this.$store.getters["contract/currentContract"];
    },
    feedbackContract() {
      if (this.contract.id) return this.feedbackContracts.find(c => c.id == this.contract.id);
      return {};
    },
    showDownloadButton() {
      return !!this.totalItems;
    },
    openOffcanvas() {
      return this.$store.getters["documentation/documentationRender"]?.["management_responses"]?.open;
    },
    comments() {
      return this.cache[this.params.page].reduce((res, item) => {
        if (item.type != "review") return res;
        res[item.id] = {};
        item.comments.forEach(c => {
          if (item.translated && this.translations[this.$i18n.locale]?.[`review_${item.id}`]?.[`comment_${c.kind}`])
            res[item.id][c.kind] = { ...c, text: this.translations[this.$i18n.locale][`review_${item.id}`][`comment_${c.kind}`] };
          else
            res[item.id][c.kind] = { ...c, text: c.comment };
        });
        return res;
      }, {});
    },
    idOnReviewSiteAndLoading() {
      return [this.idOnReviewSite, this.loading]
    },
    selectedItem() {
      if (!this.loading) return this.cache[this.params.page]?.find(i => i.id == this.selectedItemId);
      return {};
    },
    extensionEvents() {
      return {
        "OLY_DASHBOARD_GENERATE_RESPONSE": this.generateResponseFromExtension,
        "OLY_DASHBOARD_SEND_REPLY":        this.sendReviewReplyFromExtension
      };
    }
  },
  watch: {
    contract: {
      handler: function () {
        if (this.contract?.oauth_token?.length) this.connectionLink = `https://review-connect.com/oauth?oauth_token=${this.contract.oauth_token}`;
      },
      immediate: true
    },
    "$i18n.locale": function () {
      if (!this.translations[this.$i18n.locale]) Object.values(this.cache).forEach(item => item.forEach(r => r.translated = false))
    },
    filterParams: function () {
      this.loadListener(this.filterParams);
    },
    "$route.query": {
      handler: function () {
        if (!this.insideExtension) this.selectedItemId = this.$route.query.rid;
        if (this.$route.query.ext_installed) this.setExtensionInstalled(this.$route.query.ext_installed == "true");
      },
      immediate: true,
      deep: true
    },
    extensionParams: {
      handler: function () {
        if (!Object.keys(this.extensionParams || {}).length) return;
        this.selectedItemId   = this.extensionParams.rid;
        this.idOnReviewSite   = this.extensionParams.idrv;
        this.replyText        = this.extensionParams.text;
        this.generateResponse = !!this.extensionParams.gr;
        this.sendReviewReply  = !!this.extensionParams.sr;
      },
      immediate: true,
      deep: true
    },
    idOnReviewSiteAndLoading: {
      handler: function () {
        if (this.idOnReviewSite && !this.loading && this.insideExtension)
          this.setItemByIdOnReviewSite(this.idOnReviewSite);
      },
      immediate: true
    },
    selectedItem: {
      handler: function () {
        if (this.insideExtension) {
          if (this.selectedItem?.id) this.openReplyModal(300);
          else this.closeReplyModal();
        }
      },
      immediate: true
    }
  },
};
</script>

<template>
  <div class="w-100" :class="{ 'cursor-loading': loading }">
    <div class="d-flex align-items-center justify-content-between">
      <PageHeader :title="$t('daily_operations.reviews.title')" />
      <b-dropdown v-if="connectionLink" class="px-0" size="lg" variant="link" dropleft toggle-class="text-decoration-none text-secondary border border-secondary" no-caret>
        <template #button-content>
          <i class="mdi mdi-dots-vertical color-secondary"></i>
        </template>

        <b-dropdown-text> {{ $t("daily_operations.reviews.options") }} </b-dropdown-text>

        <b-dropdown-item class="d-block" :href="connectionLink" target="_blank" rel="noopener noreferrer">
          <div class="d-flex align-items-center">
            <i class="mdi mdi-link-variant font-size-18 me-1"></i>
            <p class="m-0 font-size-14"> {{ $t("daily_operations.reviews.connect") }} </p>
          </div>
        </b-dropdown-item>
      </b-dropdown>
    </div>

    <div class="row">
      <div class="col-lg-12">
        <!-- Filters -->
        <Filters ref="filtersRef" @filter:created="saveFilterParams" @filter:update="saveFilterParams" @filter:update_page="changePage" :cols="filterCols" :preventSubmit="true" :startCollapsed="insideExtension" />

        <!-- Time line -->
        <div v-if="!filterParams.data" class="card bg-light select-group-company">{{ $t("general.select_group_or_company") }}</div>
        <div class="card" v-if="filterParams.data">
          <div class="card-body" :class="{ 'px-0': insideExtension }">
            <button v-if="showDownloadButton" class="btn btn-secondary float-end d-none d-sm-block" @click="downloadXLSX" v-b-tooltip.hover :title="$t('daily_operations.reviews.download_tooltip')">{{ $t("general.export_excel") }}</button>

            <div class="row justify-content-center" :class="{ 'my-5': !insideExtension, 'm-0': insideExtension }">
              <div class="col-lg-10">
                <ul class="verti-timeline list-unstyled" :class="{ 'm-0 border-0': insideExtension }" dir="ltr" v-if="!loading">
                  <template v-for="(item, itemIndex) in cache[params.page]" :key="item.id">
                    <Response v-if="item.type == 'response' && item.show && item.company" :response="item" :responseIndex="itemIndex" :cache="cache" :params="params" :translations="translations" :api_permissions="api_permissions" :contract="feedbackContract" @click="selectItem(item.id)" :class="{ 'review-selected': selectedItemId == item.id }" :selected="selectedItemId == item.id" :replyText="replyText" :ref="String(item.id)" :insideExtension="insideExtension" :additionalInfo="additionalInfoFields[item.type]" :polaritiesToShow="polaritiesToShow" />
                    <Review v-else-if="item.type == 'review' && item.show && item.company " :review="item" :reviewIndex="itemIndex" :cache="cache" :params="params" :translations="translations" :sources="sources" :user="user" @click="selectItem(item.id)" :class="{ 'review-selected': selectedItemId == item.id }" :ref="String(item.id)" :insideExtension="insideExtension" :comments="comments[item.id]" :additionalInfo="additionalInfoFields[item.type]" :polaritiesToShow="polaritiesToShow" />
                  </template>
                </ul>

                <div v-if="loading" class="loading" style="min-height:200px;"></div>
                <div v-else-if="!totalItems" class="d-flex align-items-center justify-content-center">
                  <span class="font-size-16">{{ $t("general.no_data") }}</span>
                </div>

                <b-pagination
                  v-if="totalItems / itemsPerPage > 0"
                  class="d-flex justify-content-center mt-5"
                  v-model="params.page"
                  :total-rows="totalItems"
                  :per-page="itemsPerPage"
                  aria-controls="responses-pages"
                  @update:modelValue="pageChange"
                ></b-pagination>

                <button id="scroll-btn" @click="scrollWindow()"> &uarr; </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- review reply modal -->
    <b-modal v-model="modals.reply.review" class="review-modal" centered header-class="border-0 pb-0" footer-class="border-0 pt-2 p-3" body-class="pt-1" :class="{ 'cursor-loading': loading, 'modal-offcanvas': openOffcanvas }" size="xl" @cancel:modelValue="closeReplyModal" @hide="closeReplyModal" scrollable :hide-footer="true">
      <template #header>
        <div class="d-flex align-items-center justify-content-between w-100">
          <h5 class="border-0 pb-0 modal-title">{{ $t("daily_operations.reviews.reply_review") }}</h5>
          <div class="d-flex align-items-center">
            <DocsButton component="management_responses" :tooltip="$t('general.help')" customClass="mdi mdi-help-circle font-size-20" />
            <button class="btn-close" type="button" @click="closeReplyModal"></button>
          </div>
        </div>
      </template>
      <ReviewReply v-if="selectedItem && selectedItem.type == 'review'" :review="selectedItem" :permittedReply="replyPermissions[selectedItem.company.id]" :replyText="replyText" :generate="generateResponse" :insideExtension="insideExtension" :responseSettings="responseSettings" :comments="comments[selectedItem.id]" :additionalInfo="additionalInfoFields.review" :sendReviewReply="sendReviewReply" @updateReply="updateReply" />
    </b-modal>

    <!-- response reply modal -->
    <b-modal v-model="modals.reply.response" centered hide-footer header-class="border-0 pb-0" body-class="pt-1" :title="$t('surveys_responses.reply_response')" size="xl">
      <ResponseReply v-if="selectedItem && selectedItem.type == 'response'" :response="selectedItem" :contract="contract" />
    </b-modal>

    <!-- note modal -->
    <b-modal v-model="modals.note" centered hide-footer header-class="border-0 pb-0" body-class="pt-1" :title="$t('daily_operations.reviews.add_note')" size="xl" @update:modelValue="note = ''">
      <div v-if="selectedItem && (selectedItem.type == 'review')" class="pb-2 pt-3 d-flex flex-column">
        <span>{{ $t("daily_operations.reviews.note_explanation") }}</span>
        <div class="position-relative mt-1">
          <textarea class="w-100 reply-textarea" v-model="note" rows="7" :placeholder="$t('daily_operations.reviews.note_placeholder')"></textarea>
          <div v-if="loadingNote" class="loading cursor-loading position-absolute bg-white opacity-75 h-100 w-100" style="bottom:0;"></div>
        </div>

        <div class="d-sm-flex align-items-center justify-content-between mt-3 ps-1">
          <b-form-checkbox v-model="emailUsers" :id="`review_${selectedItem.id}_email_users`">
            <label class="form-label m-0" :for="`review_${selectedItem.id}_email_users`">{{ $t("daily_operations.reviews.email_users") }}</label>
          </b-form-checkbox>
        </div>

        <div class="footer-nav d-flex justify-content-between mt-3 mb-1">
          <button class="btn btn-outline-secondary" @click="modals.note = false">
            {{ $t("surveys_list.cancel") }}
          </button>

          <button class="btn btn-primary float-end" :disabled="note.length <= 0 || loadingNote" @click="saveNote">{{ $t("daily_operations.reviews.save_note") }}</button>
        </div>
      </div>
    </b-modal>
  </div>
</template>
